import { useEffect, useState, useCallback } from "react";
import { CheckCircle2, HandCoins, PiggyBank, Plus, XCircle } from "lucide-react";
import api, { fmtRp } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, StatCard, Badge, Empty } from "@/components/ui-kit";

export default function SavingsLoans() {
  const { user } = useAuth();
  const isAdmin = user.role === "ADMIN";
  const [savings, setSavings] = useState({ items: [], balance: 0 });
  const [loans, setLoans] = useState({ items: [], payments: [], outstanding: 0 });
  const [savingForm, setSavingForm] = useState({ member_email: "nelayan@demo.syncoop.id", entry_type: "WAJIB", direction: "DEPOSIT", amount: "", notes: "" });
  const [loanForm, setLoanForm] = useState({ amount: "", purpose: "", tenor_months: 6, member_email: "" });
  const [paying, setPaying] = useState(null);
  const [payAmount, setPayAmount] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get("/savings").then((r) => setSavings(r.data)).catch(() => {});
    api.get("/loans").then((r) => setLoans(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const submitSaving = async () => {
    setBusy(true);
    try {
      await api.post("/savings", { ...savingForm, amount: Number(savingForm.amount) });
      setSavingForm({ member_email: "nelayan@demo.syncoop.id", entry_type: "WAJIB", direction: "DEPOSIT", amount: "", notes: "" });
      load();
    } catch (e) { alert(e?.response?.data?.detail || "Gagal mencatat simpanan"); }
    finally { setBusy(false); }
  };

  const submitLoan = async () => {
    setBusy(true);
    try {
      const body = { amount: Number(loanForm.amount), purpose: loanForm.purpose, tenor_months: Number(loanForm.tenor_months) };
      if (isAdmin && loanForm.member_email) body.member_email = loanForm.member_email;
      await api.post("/loans", body);
      setLoanForm({ amount: "", purpose: "", tenor_months: 6, member_email: "" });
      load();
    } catch (e) { alert(e?.response?.data?.detail || "Gagal mengajukan pinjaman"); }
    finally { setBusy(false); }
  };

  const decide = async (loan, action) => {
    try { await api.post(`/loans/${loan.loan_id}/${action}`, { notes: action === "approve" ? "Disetujui admin koperasi" : "Belum memenuhi syarat" }); load(); }
    catch { alert("Gagal memproses pinjaman"); }
  };

  const payLoan = async () => {
    try {
      await api.post(`/loans/${paying.loan_id}/payments`, { amount: Number(payAmount), payment_method: "CASH" });
      setPaying(null); setPayAmount(""); load();
    } catch (e) { alert(e?.response?.data?.detail || "Gagal mencatat cicilan"); }
  };

  return (
    <div data-testid="savings-loans-page">
      <PageHeader
        kicker="Simpan Pinjam"
        title="Simpanan & Pinjaman"
        desc={isAdmin ? "Kelola modal anggota, pengajuan pinjaman, dan cicilan." : "Pantau simpanan dan ajukan pinjaman koperasi."}
      />

      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-px bg-[var(--line)] border hairline mb-8">
        <Cell><StatCard label="Saldo Simpanan" value={fmtRp(savings.balance)} /></Cell>
        <Cell><StatCard label="Outstanding Pinjaman" value={fmtRp(loans.outstanding)} index={1} danger={loans.outstanding > 0} /></Cell>
        <Cell><StatCard label="Pengajuan" value={loans.items.filter((l) => l.status === "PENDING").length} index={2} /></Cell>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-8">
        <section>
          <div className="flex items-center gap-2 mb-4">
            <PiggyBank size={18} />
            <h2 className="font-bold text-xl">Simpanan</h2>
          </div>
          {isAdmin && (
            <div className="border hairline p-4 sm:p-5 mb-5 grid grid-cols-1 md:grid-cols-2 gap-3">
              <input className="field tap px-4" placeholder="Email anggota" value={savingForm.member_email} onChange={(e) => setSavingForm({ ...savingForm, member_email: e.target.value })} />
              <input className="field tap px-4" type="number" placeholder="Nominal" value={savingForm.amount} onChange={(e) => setSavingForm({ ...savingForm, amount: e.target.value })} />
              <select className="field tap px-4" value={savingForm.entry_type} onChange={(e) => setSavingForm({ ...savingForm, entry_type: e.target.value })}>
                <option value="POKOK">Pokok</option><option value="WAJIB">Wajib</option><option value="SUKARELA">Sukarela</option>
              </select>
              <select className="field tap px-4" value={savingForm.direction} onChange={(e) => setSavingForm({ ...savingForm, direction: e.target.value })}>
                <option value="DEPOSIT">Setor</option><option value="WITHDRAWAL">Tarik</option>
              </select>
              <button disabled={!savingForm.amount || busy} onClick={submitSaving} className="tap btn-primary font-semibold md:col-span-2 disabled:opacity-40">
                <Plus size={16} className="inline mr-2" /> Catat Simpanan
              </button>
            </div>
          )}
          <Ledger items={savings.items} empty="Belum ada riwayat simpanan." />
        </section>

        <section>
          <div className="flex items-center gap-2 mb-4">
            <HandCoins size={18} />
            <h2 className="font-bold text-xl">Pinjaman</h2>
          </div>
          <div className="border hairline p-4 sm:p-5 mb-5 grid grid-cols-1 md:grid-cols-2 gap-3">
            {isAdmin && <input className="field tap px-4 md:col-span-2" placeholder="Email anggota (opsional)" value={loanForm.member_email} onChange={(e) => setLoanForm({ ...loanForm, member_email: e.target.value })} />}
            <label className="flex flex-col gap-2">
              <span className="mono-label">Nominal pinjaman</span>
              <input className="field tap px-4" type="number" placeholder="Contoh: 1500000" value={loanForm.amount} onChange={(e) => setLoanForm({ ...loanForm, amount: e.target.value })} />
            </label>
            <label className="flex flex-col gap-2">
              <span className="mono-label">Tenor pinjaman (bulan)</span>
              <input className="field tap px-4" type="number" min="1" placeholder="Contoh: 6 bulan" value={loanForm.tenor_months} onChange={(e) => setLoanForm({ ...loanForm, tenor_months: e.target.value })} />
            </label>
            <textarea className="field px-4 py-3 md:col-span-2" rows={2} placeholder="Tujuan pinjaman" value={loanForm.purpose} onChange={(e) => setLoanForm({ ...loanForm, purpose: e.target.value })} />
            <button disabled={!loanForm.amount || !loanForm.purpose || busy} onClick={submitLoan} className="tap btn-primary font-semibold md:col-span-2 disabled:opacity-40">Ajukan Pinjaman</button>
          </div>

          {loans.items.length === 0 ? <Empty>Belum ada pinjaman.</Empty> : (
            <div className="border hairline divide-y divide-[var(--line)]">
              {loans.items.map((l) => (
                <div key={l.loan_id} className="p-5">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-semibold">{l.member_name}</p>
                        <Badge tone={l.status === "REJECTED" ? "danger" : l.status === "PENDING" ? "outline" : "ok"}>{l.status}</Badge>
                      </div>
                      <p className="text-sm text-[var(--muted)] mt-1">{l.purpose} · {l.tenor_months} bulan</p>
                    </div>
                    <div className="text-right">
                      <p className="swiss-display text-2xl">{fmtRp(l.amount)}</p>
                      <p className="mono-label">Sisa {fmtRp(l.outstanding_balance)}</p>
                    </div>
                  </div>
                  {isAdmin && (
                    <div className="flex flex-wrap gap-2 mt-4">
                      {l.status === "PENDING" && <>
                        <button onClick={() => decide(l, "approve")} className="btn-outline px-3 py-2 text-sm font-semibold"><CheckCircle2 size={14} className="inline mr-1" /> Setujui</button>
                        <button onClick={() => decide(l, "reject")} className="btn-outline px-3 py-2 text-sm font-semibold"><XCircle size={14} className="inline mr-1" /> Tolak</button>
                      </>}
                      {["APPROVED", "DISBURSED"].includes(l.status) && <button onClick={() => setPaying(l)} className="btn-primary px-3 py-2 text-sm font-semibold">Catat Cicilan</button>}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </section>
      </div>

      {paying && (
        <div className="fixed inset-0 bg-black/40 z-50 grid place-items-center p-4" onClick={() => setPaying(null)}>
          <div className="bg-white border hairline p-6 w-full max-w-sm" onClick={(e) => e.stopPropagation()}>
            <h3 className="swiss-display text-2xl">Catat Cicilan</h3>
            <p className="text-sm text-[var(--muted)] mt-2">{paying.member_name} · sisa {fmtRp(paying.outstanding_balance)}</p>
            <input className="field tap w-full px-4 mt-5" type="number" placeholder="Nominal cicilan" value={payAmount} onChange={(e) => setPayAmount(e.target.value)} />
            <button onClick={payLoan} disabled={!payAmount} className="tap btn-primary w-full mt-4 font-semibold disabled:opacity-40">Simpan</button>
          </div>
        </div>
      )}
    </div>
  );
}

function Ledger({ items, empty }) {
  if (items.length === 0) return <Empty>{empty}</Empty>;
  return (
    <div className="border hairline divide-y divide-[var(--line)]">
      {items.map((s) => (
        <div key={s.entry_id} className="p-4 flex items-center justify-between gap-4">
          <div>
            <p className="font-semibold">{s.member_name}</p>
            <p className="text-sm text-[var(--muted)]">{s.entry_type} · {s.direction === "DEPOSIT" ? "Setor" : "Tarik"}</p>
          </div>
          <p className={`font-bold ${s.direction === "WITHDRAWAL" ? "text-[var(--danger)]" : "text-[var(--ok)]"}`}>{fmtRp(s.amount)}</p>
        </div>
      ))}
    </div>
  );
}

const Cell = ({ children }) => <div className="bg-white">{children}</div>;
